import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Lock, Check, X } from "lucide-react";

/**
 * Public-facing summary of Tyvena's Provider Tier Policy.
 * Full internal doc lives at /app/memory/PROVIDER_TIER_POLICY.md.
 */
export default function TierPolicyPage() {
  const navigate = useNavigate();

  const tiers = [
    { n: "Tier 0", name: "Listed",     price: "Free",         includes: "Business name + phone under the correct care type in the correct service area." },
    { n: "Tier 1", name: "Essential",  price: "$499/yr",      includes: "Full description, logo, website + email links, hours." },
    { n: "Tier 2", name: "Enhanced",   price: "$799/yr",      includes: "Everything in Essential + photo gallery, services list, insurance accepted, languages." },
    { n: "Tier 3", name: "Premium",    price: "$1,999/yr",    includes: "Everything in Enhanced + intro video, in-app Virtual Consult booking, detailed analytics." },
    { n: "Tier 4", name: "Enterprise", price: "Contact us",   includes: "For organizations with multiple locations or service lines. Contact us for a tailor-made experience." },
  ];

  const excluded = [
    "Preferential ranking, sort order, or search position",
    "\u201CFeatured,\u201D \u201CRecommended,\u201D \u201CTop pick,\u201D or \u201CSponsored\u201D badges in family results",
    "Exclusive or first access to family inquiries",
    "Any influence on Asher's care-type or provider guidance",
    "Volume-based, per-lead, per-inquiry, or per-admission pricing",
    "Percentage-of-revenue arrangements",
    "Suppression or downranking of non-paying providers",
    "Any signal to families that a listed provider is better than any other listed provider",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6]">
      <header className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <Button
            data-testid="policy-back-btn"
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-primary hover:bg-primary/5"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-bold text-primary">Provider Tier Policy</h1>
            <p className="text-xs font-body text-text-secondary">Fair-placement pledge · Effective February 2026</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
        {/* Pledge */}
        <section
          data-testid="policy-pledge"
          className="rounded-2xl border-2 border-primary/25 bg-primary/5 p-6 md:p-8"
        >
          <div className="flex items-start gap-3">
            <Lock className="w-6 h-6 text-primary shrink-0 mt-1" />
            <div>
              <h2 className="text-xl font-heading font-bold text-primary mb-2">Families come first — that&apos;s our pledge to them, and to you.</h2>
              <p className="font-body text-text-secondary leading-relaxed">
                Every patient has the right to choose their own provider — Medicare guarantees it, and Tyvena was built to support that 100%. No leads, no auto-matching, no pressure — a family contacts you when <em>they</em> decide you&apos;re the right fit, on their terms. Paid tiers <strong>never</strong> influence search order, sort position, or Asher&apos;s guidance. Higher tiers unlock <em>the depth of the provider&apos;s own profile</em> — photos, video, virtual consult booking — not visibility relative to other providers. Listings sort <strong>alphabetically</strong> for every family, every search.
              </p>
            </div>
          </div>
        </section>

        {/* Tier table */}
        <section>
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-4">Tier Structure</h2>
          <div className="space-y-3">
            {tiers.map((t) => (
              <div
                key={t.name}
                data-testid={`policy-tier-${t.name.toLowerCase()}`}
                className="rounded-xl border border-[#E2E8F0] bg-white p-5"
              >
                <div className="flex flex-wrap items-baseline gap-3 mb-1">
                  <span className="text-xs font-body font-bold uppercase tracking-wide text-text-secondary">
                    {t.n}
                  </span>
                  <h3 className="text-lg font-heading font-bold text-text-primary">{t.name}</h3>
                  <span className="text-sm font-body font-semibold text-primary">{t.price}</span>
                </div>
                <p className="text-sm font-body text-text-secondary">{t.includes}</p>
              </div>
            ))}
          </div>
        </section>

        {/* What paid tiers can include */}
        <section>
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-4 flex items-center gap-2">
            <Check className="w-6 h-6 text-primary" />
            What paid tiers CAN unlock
          </h2>
          <p className="font-body text-text-secondary leading-relaxed">
            Depth of the provider&apos;s own profile: description, logo, hours, photos, services list,
            insurance accepted, languages, intro video, in-app Virtual Consult booking, and provider
            analytics. All of these appear <strong>inside</strong> the provider&apos;s profile view — they
            do not change how the provider appears in search results.
          </p>
        </section>

        {/* What paid tiers can NEVER include */}
        <section>
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-4 flex items-center gap-2">
            <X className="w-6 h-6 text-red-600" />
            What paid tiers can NEVER include
          </h2>
          <ul className="space-y-2">
            {excluded.map((line, i) => (
              <li key={i} data-testid={`policy-excluded-${i}`} className="flex items-start gap-2">
                <X className="w-4 h-4 text-red-600 shrink-0 mt-1" />
                <span className="font-body text-text-secondary">{line}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Asher's guidance */}
        <section>
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-3">Asher&apos;s Guidance</h2>
          <p className="font-body text-text-secondary leading-relaxed">
            Asher never sees provider tier when recommending a care type, and never recommends
            specific provider names. Families receive a full alphabetical list matching their care
            type and zip code.
          </p>
        </section>

        {/* Editorial library */}
        <section>
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-3">Educational Library</h2>
          <p className="font-body text-text-secondary leading-relaxed">
            Any active listed provider — free or paid — is welcome to submit articles for potential
            inclusion in the Tyvena educational library. Every submission is reviewed by the Tyvena
            editorial team before publishing. Editorial decisions are based on educational merit and
            family value, never on tier or payment.
          </p>
        </section>
      </main>
    </div>
  );
}
